import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Button, Icon, Label, Segment, Message, Loader, Modal } from 'semantic-ui-react';
import { QRCodeSVG } from 'qrcode.react';

import Config from '../../../constants/Config';
import selectors from '../../../selectors';
import socket from '../../../api/socket';
import AssignFormsModal from './AssignFormsModal';
import FillInspectionModal from './FillInspectionModal';
import styles from './InspectionForms.module.scss';

const InspectionForms = React.memo(({ cardId, onResponsesLoaded }) => {
  const accessToken = useSelector(selectors.selectAccessToken);
  const [assignments, setAssignments] = useState([]);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [fillInspectionAssignment, setFillInspectionAssignment] = useState(null);

  const loadData = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setIsLoading(true);
    try {
      const assignmentsResponse = await fetch(
        `${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (assignmentsResponse.ok) {
        const assignmentsData = await assignmentsResponse.json();
        setAssignments(assignmentsData.items || []);
      }

      const responsesResponse = await fetch(
        `${Config.SERVER_BASE_URL}/api/cards/${cardId}/responses`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (responsesResponse.ok) {
        const responsesData = await responsesResponse.json();
        const items = responsesData.items || [];
        setResponses(items);

        if (onResponsesLoaded) {
          onResponsesLoaded(items);
        }
      }
    } catch (error) {
      console.error('Error loading inspection data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [cardId, accessToken, onResponsesLoaded]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleFormAssignment = data => {
      if (data.cardId === cardId) {
        loadData();
      }
    };

    socket.on('cardFormAssignment', handleFormAssignment);

    return () => {
      socket.off('cardFormAssignment', handleFormAssignment);
    };
  }, [cardId, loadData]);

  const handleUnassignForm = useCallback(async () => {
    if (!window.confirm('Are you sure you want to unassign this form?')) {
      return;
    }

    try {
      const response = await fetch(`${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms/unassign`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error unassigning form:', error);
    }
  }, [cardId, accessToken, loadData]);

  const handleGenerateQR = useCallback(async () => {
    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/cards/${cardId}/inspection/generate-qr`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            expiresInHours: 72,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setQrData(data);
        setShowQrModal(true);
      }
    } catch (error) {
      console.error('Error generating QR:', error);
    }
  }, [cardId, accessToken]);

  if (isLoading) {
    return (
      <Segment className={styles.wrapper}>
        <Loader active inline="centered" size="small" />
      </Segment>
    );
  }

  return (
    <Segment className={styles.wrapper}>
      <div className={styles.header}>
        <h4>
          <Icon name="file alternate" />
          Forms
        </h4>
      </div>

      {assignments.length === 0 ? (
        <Message info size="small">
          <Message.Header>No forms assigned</Message.Header>
          <p>Assign forms to collect structured data for this card</p>
        </Message>
      ) : (
        <div className={styles.inspectionsContainer}>
          {assignments.map(assignment => {
            const form = assignment.formId;
            const formResponses = responses.filter(r => r.formId?.id === form.id);

            return (
              <div key={assignment.id} className={styles.inspectionItem}>
                <div className={styles.inspectionHeader}>
                  <div>
                    <span className={styles.formName}>{form.name}</span>
                  </div>
                  <div className={styles.responseCount}>
                    <Icon name="file alternate outline" />
                    {formResponses.length} {formResponses.length === 1 ? 'response' : 'responses'}
                  </div>
                </div>

                <div className={styles.inspectionActions}>
                  <Button
                    size="small"
                    basic
                    icon
                    labelPosition="left"
                    onClick={() => setFillInspectionAssignment(assignment)}
                  >
                    <Icon name="pencil" />
                    Fill Inspection
                  </Button>
                  <Button
                    size="small"
                    basic
                    icon
                    labelPosition="left"
                    onClick={() => handleGenerateQR()}
                  >
                    <Icon name="qrcode" />
                    Generate QR
                  </Button>
                  <Button
                    size="small"
                    basic
                    color="red"
                    icon
                    labelPosition="left"
                    onClick={handleUnassignForm}
                  >
                    <Icon name="remove" />
                    Unassign
                  </Button>
                </div>

                {formResponses.length > 0 && (
                  <div className={styles.lastInspection}>
                    <small>
                      <Icon name="clock outline" />
                      Last completed: {new Date(
                        formResponses[0].submittedAt
                      ).toLocaleString()} by {formResponses[0].submittedByName || 'External user'}
                      {formResponses[0].triggeredCardMove && (
                        <Label size="mini" color="orange" style={{ marginLeft: '0.5rem' }}>
                          <Icon name="arrow right" />
                          Auto-moved
                        </Label>
                      )}
                    </small>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.footer}>
        <Button
          size="small"
          basic
          icon
          labelPosition="left"
          onClick={() => setShowAssignModal(true)}
        >
          <Icon name="plus" />
          {assignments.length === 0 ? 'Assign Form' : 'Change Form'}
        </Button>
      </div>

      {showAssignModal && (
        <AssignFormsModal
          cardId={cardId}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            setShowAssignModal(false);
            loadData();
          }}
        />
      )}

      {showQrModal && qrData && (
        <Modal open onClose={() => setShowQrModal(false)} size="small">
          <Modal.Header>Public Inspection Link</Modal.Header>
          <Modal.Content className={styles.qrModalContent}>
            <Message info>
              <Message.Header>Scan this QR code</Message.Header>
              <p>Operarios pueden llenar la inspección desde cualquier dispositivo sin login</p>
            </Message>

            <div className={styles.qrCodeContainer}>
              <QRCodeSVG
                value={qrData.publicUrl}
                size={256}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>

            <Message>
              <p>
                <strong>Link público:</strong>
              </p>
              <code>{qrData.publicUrl}</code>
              <p>
                <small>Expires: {new Date(qrData.expiresAt).toLocaleString()}</small>
              </p>
            </Message>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setShowQrModal(false)}>Close</Button>
            <Button
              primary
              onClick={() => {
                navigator.clipboard.writeText(qrData.publicUrl);
                alert('Link copied to clipboard');
              }}
            >
              Copy Link
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {fillInspectionAssignment && (
        <FillInspectionModal
          assignment={fillInspectionAssignment}
          cardId={cardId}
          onClose={() => setFillInspectionAssignment(null)}
          onSuccess={() => {
            setFillInspectionAssignment(null);
            loadData();
          }}
        />
      )}
    </Segment>
  );
});

InspectionForms.propTypes = {
  cardId: PropTypes.string.isRequired,
  onResponsesLoaded: PropTypes.func,
};

export default InspectionForms;
